/**
 * TabsPlugin - authoring support for any `team-tabs` tab group on the canvas.
 *
 * Matches by the Stimulus attributes the front end uses
 * (`data-controller="team-tabs"`, `data-team-tabs-target="tab|panel"`,
 * `data-tab` / `data-panel`), so it covers both the "Tabs" block
 * (template_id: "tabs", whose panels carry a `<!-- drop -->` marker) and
 * hand-written tab markup pasted into a page. What this plugin adds:
 *
 * - Editor-only panel switching. Tailwind IS loaded into the TinyMCE iframe
 *   (see TinyMCELoader `content_css`), so the `hidden` class that marks the
 *   saved default tab genuinely hides the other panels while editing. Rather
 *   than toggling `hidden` - which would make "the tab I was last editing"
 *   silently become "the tab visitors land on" - previewing moves a transient
 *   `data-wps-preview` attribute, and editor-only CSS overrides `display` for
 *   it. `data-wps-preview` is stripped on save.
 * - Tab add / remove / rename / reorder / set-default, driven by the Tabs panel
 *   in the blocks sidebar.
 *
 * All the DOM work lives here rather than in TinyMCELoader.createBlocksSidebar's
 * Alpine `x-data` template literal: that string is opaque to esbuild, so syntax
 * errors inside it only surface at runtime. The sidebar calls `window.WpsTabs`
 * one-liners instead.
 */

// Mirrors app/views/shared/_tabs.html.erb, whose classes the `team-tabs`
// Stimulus controller adds and removes on click. Keep them in sync or the
// server-rendered active tab won't match what the controller does to it.
const TAB_BASE_CLASSES = ['wps-tab', 'px-6', 'py-4', 'font-semibold', 'focus:outline-none'];
const TAB_ACTIVE_CLASSES = ['text-green-600', 'border-b-4', 'border-green-600'];
const TAB_INACTIVE_CLASSES = ['text-gray-600', 'hover:text-gray-900'];

const ROOT_SELECTOR = '[data-controller~="team-tabs"]';
const TAB_SELECTOR = '[data-team-tabs-target="tab"][data-tab]';
const PANEL_SELECTOR = '[data-team-tabs-target="panel"][data-panel]';

export class TabsPlugin {
    constructor(editor) {
        this.editor = editor;
    }

    init() {
        this.injectStyles();
        this.attachCanvasHandlers();
        this.attachSaveCleanup();
        this.watchForNewBlocks();
        this.primeExistingBlocks();

        // Single global entry point for the sidebar. Last editor to initialise
        // wins, which matches the sidebar - there is one sidebar per page.
        window.WpsTabs = this;

        console.log('✅ TabsPlugin initialized for editor:', this.editor.id);
    }

    /**
     * Editor-only CSS. Never written into saved content.
     */
    injectStyles() {
        const doc = this.editor.getDoc();
        if (!doc) return;

        const styleId = 'wps-tabs-editor-styles';
        if (doc.getElementById(styleId)) return;

        const style = doc.createElement('style');
        style.id = styleId;
        style.textContent = `
            ${ROOT_SELECTOR} ${PANEL_SELECTOR} { display: none !important; }
            ${ROOT_SELECTOR} ${PANEL_SELECTOR}[data-wps-preview] {
                display: block !important;
                outline: 1px dashed #c7d2fe;
                outline-offset: 4px;
            }
            ${ROOT_SELECTOR} ${TAB_SELECTOR} { cursor: pointer; }
        `;
        doc.head.appendChild(style);
    }

    /**
     * Clicking a tab on the canvas switches which panel you are editing.
     */
    attachCanvasHandlers() {
        this.editor.on('click', (e) => {
            const button = e.target.closest && e.target.closest(TAB_SELECTOR);
            if (!button) return;

            const root = this.rootFor(button);
            if (!root) return;

            this.preview(root, button.getAttribute('data-tab'));

            // Let an open blocks sidebar switch to this group's Tabs panel, the
            // same way TemplateClickPlugin does for blocks. Harmless when closed.
            document.dispatchEvent(new CustomEvent('template-selected', {
                detail: {
                    instanceId: this.instanceId(root),
                    templateId: 'tabs',
                    templateName: 'Tabs',
                    element: root,
                    clickedElement: button
                },
                bubbles: true
            }));
        });
    }

    /**
     * Strip the transient preview marker before content is serialized, the same
     * way DropZonePlugin.cleanupForSave strips data-wps-tree-id / -highlight.
     */
    attachSaveCleanup() {
        this.editor.on('GetContent', (e) => {
            if (!e.content || typeof e.content !== 'string') return;
            if (e.content.indexOf('data-wps-preview') === -1) return;

            const temp = this.editor.getDoc().createElement('div');
            temp.innerHTML = e.content;
            temp.querySelectorAll('[data-wps-preview]').forEach((el) => el.removeAttribute('data-wps-preview'));
            e.content = temp.innerHTML;
        });
    }

    /**
     * A block that has just been loaded from the database or dropped onto the
     * canvas carries no preview marker, so the editor CSS above would hide every
     * one of its panels. Open each such block on its default tab.
     *
     * Bound to content changes as well as init, because a drop happens long
     * after the plugin starts up.
     */
    watchForNewBlocks() {
        this.editor.on('SetContent NodeChange change', () => this.primeExistingBlocks());
    }

    primeExistingBlocks() {
        const body = this.editor.getBody();
        if (!body) return;

        body.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
            if (this.panels(root).some((p) => p.hasAttribute('data-wps-preview'))) return;
            const tabs = this.list(root);
            const initial = tabs.find((t) => t.isDefault) || tabs[0];
            if (initial) this.preview(root, initial.id, {silent: true});
        });
    }

    // ---------------------------------------------------------------- helpers

    /**
     * Nearest tabs block containing +el+, if any.
     */
    rootFor(el) {
        return el && el.closest ? el.closest(ROOT_SELECTOR) : null;
    }

    /**
     * The sidebar hands us whatever element was selected, which may be the block
     * wrapper rather than the .wps-tabs div itself.
     */
    resolveRoot(el) {
        if (!el) return null;
        return this.rootFor(el) || (el.querySelector ? el.querySelector(ROOT_SELECTOR) : null);
    }

    /**
     * Buttons / panels belonging to +root+ itself, not to a tab group nested
     * inside one of its panels. Hand-written markup nests the buttons several
     * levels deep, so this walks descendants and filters by nearest root.
     */
    buttons(root) {
        return Array.prototype.slice.call(root.querySelectorAll(TAB_SELECTOR))
            .filter((b) => this.rootFor(b) === root);
    }

    panels(root) {
        return Array.prototype.slice.call(root.querySelectorAll(PANEL_SELECTOR))
            .filter((p) => this.rootFor(p) === root);
    }

    /**
     * The Tabs block owns its styling (TAB_*_CLASSES). Anything else keeps
     * whatever classes the author wrote.
     */
    isWpsBlock(root) {
        return root.classList.contains('wps-tabs');
    }

    buttonFor(root, tabId) {
        return this.buttons(root).find((b) => b.getAttribute('data-tab') === tabId) || null;
    }

    panelFor(root, tabId) {
        return this.panels(root).find((p) => p.getAttribute('data-panel') === tabId) || null;
    }

    /**
     * `tab-1`, `tab-2`, ... - a scan of the existing ids rather than a timestamp
     * or random suffix, so ids stay readable and stable in saved HTML.
     */
    nextTabId(root) {
        const used = this.buttons(root).map((b) => b.getAttribute('data-tab') || '');
        let n = used.length + 1;
        while (used.indexOf('tab-' + n) !== -1) n++;
        return 'tab-' + n;
    }

    instanceId(root) {
        const wrapper = root.closest('[data-template-instance]');
        return (wrapper && wrapper.getAttribute('data-template-instance')) || this.editor.id;
    }

    changed() {
        this.editor.dispatch('change');
    }

    // ------------------------------------------------------------ public API

    /**
     * @returns {Array<{id: string, label: string, isDefault: boolean}>}
     */
    list(el) {
        const root = this.resolveRoot(el);
        if (!root) return [];

        const buttons = this.buttons(root);
        // Hand-written markup has no data-wps-default; the default is the panel
        // that isn't hidden.
        const hasExplicitDefault = buttons.some((b) => b.hasAttribute('data-wps-default'));

        return buttons.map((button) => {
            const id = button.getAttribute('data-tab');
            const panel = this.panelFor(root, id);
            return {
                id: id,
                label: (button.textContent || '').trim(),
                isDefault: hasExplicitDefault
                    ? button.hasAttribute('data-wps-default')
                    : !!panel && !panel.classList.contains('hidden'),
                isPreviewing: !!panel && panel.hasAttribute('data-wps-preview')
            };
        });
    }

    add(el) {
        const root = this.resolveRoot(el);
        if (!root) return null;

        const existingButtons = this.buttons(root);
        const existingPanels = this.panels(root);
        const nav = existingButtons.length
            ? existingButtons[existingButtons.length - 1].parentNode
            : root.querySelector('.wps-tabs-nav');
        const panelsWrap = existingPanels.length
            ? existingPanels[existingPanels.length - 1].parentNode
            : root.querySelector('.wps-tabs-panels');
        if (!nav || !panelsWrap) return null;

        const doc = root.ownerDocument;
        const tabId = this.nextTabId(root);

        const button = doc.createElement('button');
        button.setAttribute('type', 'button');
        button.className = this.newButtonClasses(root, existingButtons);
        button.setAttribute('data-team-tabs-target', 'tab');
        button.setAttribute('data-action', 'click->team-tabs#show');
        button.setAttribute('data-tab', tabId);
        button.textContent = 'New Tab';
        nav.appendChild(button);

        const panel = doc.createElement('div');
        panel.className = this.newPanelClasses(root, existingPanels);
        panel.setAttribute('data-team-tabs-target', 'panel');
        panel.setAttribute('data-panel', tabId);
        // The empty paragraph is a caret target, so the author can type or paste
        // a {partial: ...} shortcode straight into the tab.
        panel.innerHTML = '<p><br></p>' + this.dropZoneHTML(root);
        panelsWrap.appendChild(panel);

        this.preview(root, tabId, {silent: true});
        this.changed();
        return tabId;
    }

    /**
     * Class list for a freshly added, inactive tab button: the block's own
     * constants, or a copy of an existing inactive button's classes so a new
     * tab in hand-written markup matches its siblings.
     */
    newButtonClasses(root, buttons) {
        if (this.isWpsBlock(root) || !buttons.length) {
            return TAB_BASE_CLASSES.concat(TAB_INACTIVE_CLASSES).join(' ');
        }
        const inactive = buttons.find((b) => !this.isDefaultButton(root, b)) || buttons[buttons.length - 1];
        return inactive.className;
    }

    newPanelClasses(root, panels) {
        if (this.isWpsBlock(root) || !panels.length) return 'wps-tab-panel hidden';
        const hidden = panels.find((p) => p.classList.contains('hidden'));
        if (hidden) return hidden.className;
        return (panels[0].className + ' hidden').trim();
    }

    isDefaultButton(root, button) {
        return this.list(root).some((t) => t.isDefault && t.id === button.getAttribute('data-tab'));
    }

    /**
     * Live drop zone markup, so a new tab accepts blocks immediately rather than
     * only after a setContent round-trip re-processes the `<!-- drop -->` marker.
     */
    dropZoneHTML(root) {
        const dropZonePlugin = this.editor.dropZonePlugin;
        if (dropZonePlugin && typeof dropZonePlugin.processDropZones === 'function') {
            return dropZonePlugin.processDropZones('<!-- drop -->', this.instanceId(root));
        }
        return '<!-- drop -->';
    }

    remove(el, tabId) {
        const root = this.resolveRoot(el);
        if (!root) return false;

        const buttons = this.buttons(root);
        if (buttons.length <= 1) {
            this.notify('A tabs block needs at least one tab.');
            return false;
        }

        const button = this.buttonFor(root, tabId);
        const panel = this.panelFor(root, tabId);
        if (!button) return false;

        if (panel && this.panelHasContent(panel)) {
            const label = (button.textContent || 'this tab').trim();
            if (!window.confirm('Delete "' + label + '" and everything in it?')) return false;
        }

        const wasDefault = button.hasAttribute('data-wps-default');
        const wasPreviewing = panel && panel.hasAttribute('data-wps-preview');

        button.remove();
        if (panel) panel.remove();

        const remaining = this.buttons(root);
        if (wasDefault && remaining.length) {
            this.setDefault(root, remaining[0].getAttribute('data-tab'), {silent: true});
        }
        if (wasPreviewing && remaining.length) {
            this.preview(root, remaining[0].getAttribute('data-tab'), {silent: true});
        }

        this.changed();
        return true;
    }

    /**
     * A panel holds more than the placeholder paragraph and its empty drop zone.
     */
    panelHasContent(panel) {
        const clone = panel.cloneNode(true);
        clone.querySelectorAll('.richtext-nested-dropzone').forEach((zone) => zone.remove());
        if (clone.querySelector('[data-template-instance], img, iframe')) return true;
        return (clone.textContent || '').trim().length > 0;
    }

    rename(el, tabId, label) {
        const root = this.resolveRoot(el);
        const button = root && this.buttonFor(root, tabId);
        if (!button) return false;

        const next = (label || '').trim();
        if (!next) return false;

        button.textContent = next;
        this.changed();
        return true;
    }

    /**
     * Move a tab one place earlier (-1) or later (+1), button and panel together.
     */
    reorder(el, tabId, delta) {
        const root = this.resolveRoot(el);
        if (!root) return false;

        const buttons = this.buttons(root);
        const index = buttons.findIndex((b) => b.getAttribute('data-tab') === tabId);
        const target = index + delta;
        if (index === -1 || target < 0 || target >= buttons.length) return false;

        const button = buttons[index];
        const sibling = buttons[target];
        if (delta < 0) sibling.parentNode.insertBefore(button, sibling);
        else sibling.parentNode.insertBefore(button, sibling.nextSibling);

        const panel = this.panelFor(root, tabId);
        const siblingPanel = this.panelFor(root, sibling.getAttribute('data-tab'));
        if (panel && siblingPanel) {
            if (delta < 0) siblingPanel.parentNode.insertBefore(panel, siblingPanel);
            else siblingPanel.parentNode.insertBefore(panel, siblingPanel.nextSibling);
        }

        this.changed();
        return true;
    }

    /**
     * The only writer of saved state: which tab visitors land on. Keeps the
     * button classes and the panels' `hidden` classes consistent, because
     * team_tabs_controller#connect picks the active tab by looking for
     * `border-green-600`.
     */
    setDefault(el, tabId, options) {
        const root = this.resolveRoot(el);
        if (!root || !this.buttonFor(root, tabId)) return false;

        const buttons = this.buttons(root);
        if (this.isWpsBlock(root)) {
            buttons.forEach((button) => {
                const isDefault = button.getAttribute('data-tab') === tabId;
                button.classList.remove.apply(button.classList, TAB_ACTIVE_CLASSES.concat(TAB_INACTIVE_CLASSES));
                button.classList.add.apply(button.classList, isDefault ? TAB_ACTIVE_CLASSES : TAB_INACTIVE_CLASSES);
            });
        } else {
            // Hand-written markup uses its own active/inactive class sets, so
            // swap the whole className between the old and new default button.
            const previous = buttons.find((b) => this.isDefaultButton(root, b));
            const next = this.buttonFor(root, tabId);
            if (previous && next && previous !== next) {
                const activeClasses = previous.className;
                previous.className = next.className;
                next.className = activeClasses;
            }
        }
        buttons.forEach((button) => {
            if (button.getAttribute('data-tab') === tabId) button.setAttribute('data-wps-default', 'true');
            else button.removeAttribute('data-wps-default');
        });

        this.panels(root).forEach((panel) => {
            panel.classList.toggle('hidden', panel.getAttribute('data-panel') !== tabId);
        });

        if (!(options && options.silent)) this.changed();
        return true;
    }

    /**
     * Editor-only: choose which panel is visible while editing. Deliberately
     * does not touch `hidden`, so previewing a tab never changes what visitors
     * see.
     */
    preview(el, tabId, options) {
        const root = this.resolveRoot(el);
        if (!root) return false;

        this.panels(root).forEach((panel) => {
            if (panel.getAttribute('data-panel') === tabId) panel.setAttribute('data-wps-preview', 'true');
            else panel.removeAttribute('data-wps-preview');
        });

        if (!(options && options.silent)) this.changed();
        return true;
    }

    notify(text) {
        if (this.editor.notificationManager) {
            this.editor.notificationManager.open({text: text, type: 'warning', timeout: 3000});
        } else {
            console.warn(text);
        }
    }
}
